import { Link } from "react-router-dom";
import RevealSection from "@/components/base/RevealSection";

const HERO_IMG =
  "https://readdy.ai/api/search-image?query=Two%20professional%20colleagues%20having%20a%20calm%2C%20respectful%20conversation%20in%20a%20modern%20office%20with%20large%20windows%20and%20soft%20natural%20light%2C%20one%20person%20listening%20attentively%20while%20the%20other%20speaks%20gently%2C%20neutral%20warm%20color%20palette%2C%20shallow%20depth%20of%20field%2C%20corporate%20photography%20style%2C%20peaceful%20workplace%20atmosphere%2C%20navy%20and%20teal%20tones%20in%20decor&width=1200&height=800&seq=hero1&orientation=landscape";

const COURSE_IMG =
  "https://storage.readdy-site.link/project_files/354b6cde-f378-42cf-a9dc-2356d64fa0ef/392ba19c-d7f5-4f75-b48d-9ee20426ad69_Calm-Authority-Gumroad-Thumbnail.png?v=435a86db9b09c5368dbae4a6c766e3be";

const SCRIPT_PACK_IMG =
  "https://storage.readdy-site.link/project_files/354b6cde-f378-42cf-a9dc-2356d64fa0ef/5fc61468-c321-45ef-b094-9d818fde531a_Script-Pack-Gumroad-Thumbnail.png?v=435b303b38e5e369642beb3ca8a7206c";

const SCRIPT_TYPES = ["Boundaries", "Feedback", "Conflict", "Escalation", "Repair"];

const WHO_FOR = [
  {
    title: "Employees",
    desc: "Communicate your needs clearly without damaging relationships with colleagues or managers.",
  },
  {
    title: "Managers",
    desc: "Deliver feedback, set boundaries, and navigate team conflict with confidence and care.",
  },
  {
    title: "Team Leaders",
    desc: "Model calm communication for your team and handle difficult group dynamics effectively.",
  },
  {
    title: "Business Owners",
    desc: "Address performance issues, set client boundaries, and protect your business relationships.",
  },
  {
    title: "Professionals Avoiding Conflict",
    desc: "Stop avoiding hard conversations. Get the words and confidence you need to speak up.",
  },
];

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] lg:min-h-[800px] flex flex-col lg:flex-row">
        <RevealSection className="w-full lg:w-[45%]">
          <div className="relative z-10 w-full bg-navy flex flex-col justify-center px-6 md:px-12 lg:px-16 py-20 md:py-24 h-full">
            <h1 className="ca-hero-title text-3xl md:text-4xl lg:text-[56px] font-bold text-white">
              <span className="ca-hero-title-row">Difficult Conversations</span>
              <span className="ca-hero-title-row">Without Damage</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl lg:text-2xl text-seafoam font-light leading-relaxed max-w-lg">
              Communicate with clarity. Handle conflict with confidence. Lead with
              calm authority.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/quiz"
                className="inline-flex items-center justify-center bg-teal text-white font-semibold px-8 py-4 rounded-lg hover:bg-navy transition-all duration-300 text-base whitespace-nowrap"
              >
                Take the Free Quiz
              </Link>
              <Link
                to="/decision-tree"
                className="inline-flex items-center justify-center border-2 border-white text-white font-semibold px-8 py-4 rounded-lg hover:bg-white hover:text-navy transition-all duration-300 text-base whitespace-nowrap"
              >
                Explore the Decision Tree
              </Link>
            </div>
          </div>
        </RevealSection>

        {/* Right Image */}
        <div className="relative w-full lg:w-[55%] h-[400px] md:h-[500px] lg:h-auto">
          <img
            src={HERO_IMG}
            alt="Calm workplace conversation"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/60 via-transparent to-transparent lg:block hidden" />
        </div>
      </section>

      {/* What Calm Authority Helps You Do */}
      <RevealSection>
        <section className="bg-seafoam px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-[42px] font-bold text-navy text-center mb-12 md:mb-16">
              What Calm Authority Helps You Do
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
              {[
                {
                  icon: "ri-shield-check-line",
                  title: "Prepare",
                  desc: "Enter every difficult conversation knowing exactly what to say and how to say it. No more anxiety or second-guessing.",
                },
                {
                  icon: "ri-message-3-line",
                  title: "Communicate",
                  desc: "Speak with clarity and confidence. Deliver your message in a way that gets heard without creating defensiveness.",
                },
                {
                  icon: "ri-line-chart-line",
                  title: "Improve",
                  desc: "Turn conflict into growth. Repair relationships, strengthen trust, and build better workplace dynamics.",
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="bg-white rounded-xl p-6 md:p-10 text-center transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white border-2 border-seafoam flex items-center justify-center mx-auto mb-5">
                    <i className={`${card.icon} text-teal text-2xl md:text-3xl`} />
                  </div>
                  <h3 className="text-xl md:text-2xl lg:text-[28px] font-semibold text-navy mb-3">
                    {card.title}
                  </h3>
                  <p className="text-navy/80 text-base md:text-lg leading-relaxed">
                    {card.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Who This Is For */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-[42px] font-bold text-navy mb-10 md:mb-12">
              Who This Is For
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-8">
              {WHO_FOR.map((item) => (
                <div
                  key={item.title}
                  className="bg-white border-l-[3px] border-teal rounded-r-lg p-5 md:p-7 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <h3 className="text-lg md:text-xl font-semibold text-navy mb-2">
                    {item.title}
                  </h3>
                  <p className="text-navy/70 text-sm md:text-base leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Script Pack Section */}
      <RevealSection delay={100}>
        <section className="bg-navy px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div className="w-full lg:w-[40%]">
              <div className="rounded-2xl overflow-hidden shadow-lg bg-white">
                <img
                  src={SCRIPT_PACK_IMG}
                  alt="Script Pack cover"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="w-full lg:w-[60%] text-center lg:text-left">
              <h2 className="text-2xl md:text-3xl lg:text-[40px] font-bold text-white mb-5 leading-tight">
                Practical Scripts for Every Situation
              </h2>
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 md:gap-3 mb-6">
                {SCRIPT_TYPES.map((type) => (
                  <span
                    key={type}
                    className="bg-seafoam text-navy text-sm md:text-base font-medium px-4 py-2 rounded-full whitespace-nowrap"
                  >
                    {type}
                  </span>
                ))}
              </div>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                Stop rehearsing conversations in your head. Get word-for-word scripts
                that help you handle boundaries, feedback, conflict, escalation, and
                repair with calm authority.
              </p>
              <a
                href="https://katiahunt.gumroad.com/l/scripts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-teal text-white font-semibold px-8 md:px-10 py-4 rounded-lg hover:bg-seafoam hover:text-navy transition-all duration-300 text-base whitespace-nowrap"
              >
                Get the Script Pack
              </a>
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Course Section */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div className="w-full lg:w-[40%]">
              <div className="rounded-2xl overflow-hidden shadow-lg bg-white border border-seafoam/30">
                <img
                  src={COURSE_IMG}
                  alt="Difficult Conversations Without Damage course cover"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="w-full lg:w-[60%] flex flex-col items-start">
              <h2 className="text-2xl md:text-3xl lg:text-[38px] font-bold text-navy leading-tight mb-4">
                Difficult Conversations Without Damage
              </h2>
              <p className="text-navy/70 text-base md:text-lg leading-relaxed mb-6 max-w-xl">
                A comprehensive course that teaches you how to prepare for, conduct,
                and follow up on the hardest conversations at work. Built from
                real-world coaching experience with professionals just like you.
              </p>
              <a
                href="https://katiahunt.gumroad.com/l/course"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center border-2 border-teal text-teal font-semibold px-8 py-3.5 rounded-lg hover:bg-teal hover:text-white transition-all duration-300 text-base whitespace-nowrap"
              >
                Get the Course
              </a>
            </div>
          </div>
        </section>
      </RevealSection>
    </div>
  );
}
